import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Typography,
  TablePagination,
  TextField,
  InputAdornment,
  Chip,
  TableSortLabel,
} from '@mui/material'
import { Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon } from '@mui/icons-material'
import { Empresa, Grupo } from '../../types/api'
import { EmpresaSortField } from '../../api/empresas'

interface EmpresasTableProps {
  empresas: Empresa[]
  grupos: Grupo[]
  onEdit: (empresa: Empresa) => void
  onDelete: (empresa: Empresa) => void
  loading?: boolean
  sortField: EmpresaSortField
  sortDirection: 'asc' | 'desc'
  onSort: (field: EmpresaSortField) => void
}

const EmpresasTable: React.FC<EmpresasTableProps> = ({
  empresas = [],
  grupos = [],
  onEdit,
  onDelete,
  loading,
  sortField,
  sortDirection,
  onSort,
}) => {
  const { t } = useTranslation()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [searchTerm, setSearchTerm] = useState('')

  const safeEmpresas = Array.isArray(empresas) ? empresas : []

  // Função para obter nome do grupo
  const getGrupoNome = (grupoId: number): string => {
    const grupo = grupos.find((g) => g.id === grupoId)
    return grupo?.nome || `Grupo ${grupoId}`
  }

  // Filtrar empresas por termo de busca
  const filteredEmpresas = useMemo(
    () =>
      safeEmpresas.filter(
        (empresa) =>
          empresa.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (empresa.cnpj && empresa.cnpj.includes(searchTerm)) ||
          (empresa.banco_dados &&
            empresa.banco_dados.toLowerCase().includes(searchTerm.toLowerCase()))
      ),
    [safeEmpresas, searchTerm]
  )

  // Paginação
  const paginatedEmpresas = filteredEmpresas.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  )

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const getStatusColor = (status?: string) => {
    return status === 'A' ? 'success' : 'default'
  }

  const getStatusLabel = (status?: string) => {
    if (status === 'A') return t('common.active')
    if (status === 'I') return t('common.inactive')
    return t('common.unknown')
  }

  const handleSort = (field: EmpresaSortField) => {
    onSort(field)
    setPage(0)
  }

  const columns: Array<{
    key: EmpresaSortField | 'grupo' | 'versao_banco_java'
    label: string
    align?: 'left' | 'right'
  }> = [
    { key: 'id', label: t('common.id'), align: 'right' },
    { key: 'nome', label: t('companies.name') },
    { key: 'cnpj', label: t('companies.cnpj') },
    { key: 'banco_dados', label: t('companies.database') },
    { key: 'ip', label: t('companies.ip') },
    { key: 'versao_banco_java' as any, label: 'Versão Java' },
    { key: 'status', label: t('companies.status') },
    { key: 'grupo', label: 'Nome do Grupo' },
  ]

  if (loading) {
    return (
      <Box p={3}>
        <Typography>{t('common.loading')}</Typography>
      </Box>
    )
  }

  return (
    <Paper
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }}
    >
      <Box p={2} pb={1}>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder={t('companies.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value)
            setPage(0)
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 1.5,
            '& .MuiInputBase-root': { minHeight: 28 },
            '& .MuiOutlinedInput-input': { py: 0.75 },
          }}
        />
      </Box>

      <TableContainer
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((column) => {
                const isActive = sortField === column.key
                const direction = isActive ? sortDirection : 'asc'
                const isSortable = column.key !== 'grupo'
                return (
                  <TableCell
                    key={column.key}
                    align={column.align ?? 'left'}
                    sortDirection={isActive ? sortDirection : false}
                  >
                    {isSortable ? (
                      <TableSortLabel
                        active={isActive}
                        direction={direction}
                        onClick={() => handleSort(column.key as EmpresaSortField)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                )
              })}
              <TableCell align="right">{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedEmpresas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="text.secondary" py={3}>
                    {searchTerm ? t('companies.noResults') : t('companies.noData')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedEmpresas.map((empresa) => (
                <TableRow key={empresa.id} hover>
                  <TableCell align="right">{empresa.id}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {empresa.nome}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {empresa.cnpj || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {empresa.banco_dados || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {empresa.ip || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {empresa.versao_banco_java || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(empresa.status)}
                      color={getStatusColor(empresa.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {getGrupoNome(empresa.grupo_id)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => onEdit(empresa)}
                      color="primary"
                      aria-label={t('common.edit')}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => onDelete(empresa)}
                      color="warning"
                      aria-label={t('common.deactivate', 'Inativar')}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredEmpresas.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
        labelRowsPerPage={t('common.rowsPerPage')}
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
        }
        sx={{ borderTop: (theme) => `1px solid ${theme.palette.divider}` }}
      />
    </Paper>
  )
}

export default EmpresasTable


